import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useOrganization } from '../../context/OrganizationContext';
import { fetchWithOrg } from '../../utils/api';

import {
  ArrowRight,
  CheckCircle,
  CircleNotch
} from '@phosphor-icons/react';
import { BACKEND_URL } from '../../config';

/**
 * WorkingBanner component
 * Shows "Working..." status in Chat view with link to Tasks tab
 */
export const WorkingBanner = ({ operatorId, onViewTasks }) => {
  const { activeOrganization } = useOrganization();
  const [status, setStatus] = useState({
    is_working: false,
    current_task_title: null,
    progress_pct: null,
    run_id: null,
    status: null
  });

  const fetchStatus = useCallback(async () => {
    if (!operatorId || !activeOrganization) return;
    
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/agents/${operatorId}/working-status`,
        {},
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch working status:', error);
    }
  }, [operatorId, activeOrganization]);

  // Poll for status
  useEffect(() => {
    fetchStatus();
    
    // Poll every 2 seconds when working, every 5 seconds when idle
    const interval = setInterval(fetchStatus, status.is_working ? 2000 : 5000);
    return () => clearInterval(interval);
  }, [fetchStatus, status.is_working]);

  // Don't render if not working
  if (!status.is_working && status.status !== 'waiting_for_approval') {
    return null;
  }

  const isWaiting = status.status === 'waiting_for_approval';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={`
          flex items-center gap-3 px-4 py-2 rounded-lg mb-3
          ${isWaiting 
            ? 'bg-orange-500/10 border border-orange-500/20' 
            : 'bg-blue-500/10 border border-blue-500/20'
          }
        `}
      >
        {isWaiting ? (
          <div className="w-5 h-5 rounded-full bg-orange-500/20 flex items-center justify-center">
            <span className="text-orange-400 text-xs">!</span>
          </div>
        ) : (
          <CircleNotch size={18} className="animate-spin text-blue-400" />
        )}
        
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${isWaiting ? 'text-orange-400' : 'text-blue-400'}`}>
            {isWaiting ? 'Waiting for approval' : 'Working...'}
          </p>
          {status.current_task_title && (
            <p className="text-xs text-[#A0AEC0] truncate">
              {status.current_task_title}
              {status.progress_pct !== null && ` (${status.progress_pct}%)`}
            </p>
          )}
        </div>
        
        <button
          onClick={onViewTasks}
          className={`
            flex items-center gap-1 px-3 py-1 rounded text-xs font-medium transition-colors
            ${isWaiting
              ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30'
              : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
            }
          `}
        >
          View Tasks
          <ArrowRight size={12} />
        </button>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * CompletedBanner component
 * Shows briefly when a task completes
 */
export const CompletedBanner = ({ message, onDismiss }) => {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 3000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="flex items-center gap-3 px-4 py-2 rounded-lg mb-3 bg-green-500/10 border border-green-500/20"
    >
      <CheckCircle size={18} className="text-[#00E5C0]" />
      <p className="text-sm text-[#00E5C0] flex-1">{message || 'Task completed'}</p>
    </motion.div>
  );
};

export default WorkingBanner;
